import { useEffect, useRef } from 'react';

// Tabbable elements inside the trap. Deliberately does NOT filter on offsetParent/visibility: an
// open modal's controls are visible, and jsdom reports offsetParent === null for everything, which
// would make the trap (and its tests) see zero focusables.
// CAVEAT (safe today, tracked): a real browser skips a `display:none` control in the tab order, but
// this selector still matches it. The boundary check compares against the FIRST and LAST matches, so
// it stays correct only while the DOM-first and DOM-last matches are genuinely tab-reachable. In the
// deposit modal they always are — the X close button is first and the submit button is last, with any
// hidden controls (e.g. the visually-hidden radio inputs) strictly between them. A future reorder that
// put a hidden control at a literal DOM boundary would need a visibility filter here (not addable via
// offsetParent in jsdom — would need getComputedStyle).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getTabbables = (node: HTMLElement): HTMLElement[] =>
  Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
  );

/**
 * A NODE-scoped focus trap for a modal container (WCAG 2.4.3). EDGE-32156.
 *
 * Why a hand-rolled native-DOM trap and not Mantine's `useFocusTrap`/`FocusTrap`: the deposit modal
 * (DialogModal) renders INLINE, but the crypto reveal sheet on top of it is a Mantine `Modal`
 * rendered in a PORTAL at document.body — OUTSIDE this container's DOM subtree. Mantine's trap binds
 * its Tab handler to `document` and calls `createAriaHider`, so an outer Mantine trap would (a) fight
 * the portaled inner sheet's own trap on every Tab and (b) aria-hide it while it is visible. A native
 * `keydown` listener bound to THIS node instead never sees events dispatched inside the portaled
 * sheet (native DOM events don't cross the portal boundary — React's synthetic onKeyDown would), so
 * the two traps coexist: the inner sheet manages its own focus, and this one resumes when it closes.
 * No aria-hider, so nothing is hidden from screen readers.
 *
 * While `active`, the returned ref's node (1) receives initial focus on open, (2) cycles Tab /
 * Shift+Tab within itself, and (3) returns focus to the control that was focused when the trap
 * activated (the invoker) on deactivate/unmount.
 */
export function useModalFocusTrap<T extends HTMLElement = HTMLDivElement>(active: boolean) {
  const nodeRef = useRef<T>(null);
  const invokerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const node = nodeRef.current;
    if (!active || !node) return undefined;

    // Save the invoker for return-focus (AC #2), BEFORE moving focus into the modal.
    const invoker = document.activeElement;
    invokerRef.current = invoker instanceof HTMLElement ? invoker : null;

    // Move initial focus into the modal if it isn't already inside.
    if (!node.contains(document.activeElement)) {
      const [firstTabbable] = getTabbables(node);
      (firstTabbable ?? node).focus?.({ preventScroll: true });
    }

    // Native listener on the node (NOT React onKeyDown): a portaled child modal's Tab events never
    // reach it, so nested traps don't fight. See the doc comment above.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const tabbables = getTabbables(node);
      if (tabbables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const activeEl = document.activeElement;
      if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      } else if (event.shiftKey && (activeEl === first || !node.contains(activeEl))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      }
    };

    node.addEventListener('keydown', handleKeyDown);

    return () => {
      node.removeEventListener('keydown', handleKeyDown);
      // Return focus to the invoker (AC #2) — only if it is still in the DOM and focusable, else no-op
      // (the invoker may have unmounted, e.g. a tile in a list that re-rendered).
      const invokerEl = invokerRef.current;
      invokerRef.current = null;
      if (
        invokerEl &&
        invokerEl !== document.body &&
        document.contains(invokerEl) &&
        typeof invokerEl.focus === 'function'
      ) {
        invokerEl.focus({ preventScroll: true });
      }
    };
  }, [active]);

  return nodeRef;
}
